import {
  getTimeFormatForTicks,
  getTimeInterval,
  useChartCtx
} from '@weenat/client/dist/core/charts'
import { isNil } from 'lodash-es'
import moment from 'moment-timezone'
import { FC, useMemo } from 'react'

const TEXT_CHARACTER_WIDTH = 6

const tickLabelStyle = {
  fontSize: '10px',
  textAnchor: 'middle',
  transform: 'translateY(16px)',
  fill: '#002635'
}

interface TimeAxisProps {
  tickFormat?: string
  tickCount?: number
  color?: string | ((date: Date) => string)
  timezone: string
  period: [Date, Date]
  isFourHourTimeStep?: boolean
}

const TimeAxis: FC<TimeAxisProps> = ({
  tickFormat: formatFromProps,
  tickCount,
  timezone,
  period,
  isFourHourTimeStep = false
}) => {
  const { height, marginBottom, xScale } = useChartCtx()

  const timeInterval = useMemo(() => {
    return isNil(period) ? undefined : getTimeInterval(period, timezone, isFourHourTimeStep)
  }, [period, timezone, isFourHourTimeStep])

  const tickFormat = !isNil(formatFromProps)
    ? formatFromProps
    : isNil(period)
      ? undefined
      : getTimeFormatForTicks(period, timezone)

  const ticks = useMemo(() => {
    const ticksArray = !isNil(timeInterval)
      ? xScale?.ticks(timeInterval).filter((_a, idx, vals) => {
          const ratio = !isNil(tickCount) ? Math.floor(vals.length / tickCount) : undefined
          return isNil(ratio) || idx % ratio === 0
        })
      : xScale?.ticks(tickCount)

    const distanceBtwTicks = xScale?.(ticksArray[1]) - xScale?.(ticksArray[0])

    return ticksArray
      ?.map((value) => {
        const formattedValue = moment(value).format(tickFormat)
        const labelWidth = formattedValue.length * TEXT_CHARACTER_WIDTH
        const nextTicksOverlapped = Math.floor(labelWidth / distanceBtwTicks)

        return {
          id: value.getTime(),
          value: formattedValue,
          xOffset: xScale(value),
          nextTicksOverlapped: nextTicksOverlapped
        }
      })
      .reduce(
        ({ result, skip }, val) =>
          skip > 0
            ? { result, skip: skip - 1 }
            : { result: [...result, val], skip: val.nextTicksOverlapped },
        {
          result: [] as {
            id: number
            value: string
            xOffset: number
            nextTicksOverlapped: number
          }[],
          skip: 0
        }
      ).result
  }, [timeInterval, xScale, tickCount, tickFormat])

  return (
    <g transform={`translate(0,${height - marginBottom})`}>
      {ticks?.map(({ value, xOffset }) => (
        <g key={value} transform={`translate(${xOffset}, 0)`}>
          <line y2='4' stroke={'black'} />
          <text key={value} style={tickLabelStyle}>
            {value}
          </text>
        </g>
      ))}
    </g>
  )
}

export default TimeAxis
